import React, { Fragment, useContext, useState } from 'react'
import { NameDto } from '@/api/Common/Types'
import { DataCard, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormMultiSelectField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { UpdateClientStatus } from 'api/Clients/Types/UpdateClientStatus'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { MainTemplate } from 'components/Templates/MainTemplate'
import { FormikProps, withFormik } from 'formik'
import { ChevronDown, Info } from 'lucide-react'
import { useAppSelector } from 'store'
import { AuthSessionContext } from 'utils/AuthContext'
import { FormSubmitValues } from 'utils/formValidation'
import { hasPermission } from 'utils/permissionsValidator'

import { UpdateClientOther } from '../../../../../api/Clients/Types/UpdateClientOther'
import { ClientNavigation, ClientTab } from '../../Navigation/ClientNavigation'

const ClientOtherFormUI: React.FC<FormikProps<ClientDetailsOtherFormValues> & OuterProps> = (
  props
) => {
  const {
    resetForm,
    handleSubmit,
    setSubmitting,
    setFieldValue,
    onMultiTierRegistrationAllow,
    onToggleInterestRatesRestriction,
    onToggleBonusCampaignExclusion,
    onToggleLuckyDrawExclusion,
  } = props

  const [auth] = useContext(AuthSessionContext)
  const kycProviders = useAppSelector((state) => state.clients.kycProviders)
  const restrictions = useAppSelector((state) => state.clients.restrictions)
  const [isEdit, setEdit] = useState<boolean>(false)
  const permissions = auth?.permissions || []

  const handleCancel = () => {
    setEdit(false)
    resetForm()
  }

  const handleSubmitForm = () => {
    setSubmitting(true)
    setEdit(false)
    handleSubmit()
  }

  const renderMenu = () => (
    <>
      <DropdownMenuItem
        disabled={!hasPermission(permissions, PermissionsEnum.CanEditToggleMLM)}
        onSelect={onMultiTierRegistrationAllow}
      >
        Toggle MLM Registration
      </DropdownMenuItem>
      <DropdownMenuItem
        disabled={!hasPermission(permissions, PermissionsEnum.CanEditInterestRatesRestriction)}
        onSelect={onToggleInterestRatesRestriction}
      >
        Toggle Interest Rates
      </DropdownMenuItem>
      <DropdownMenuItem
        disabled={!hasPermission(permissions, PermissionsEnum.CanAddExclusionFromBonusCampaign)}
        onSelect={onToggleBonusCampaignExclusion}
      >
        Toggle bonus campaign exclusion
      </DropdownMenuItem>
      <DropdownMenuItem
        disabled={!hasPermission(permissions, PermissionsEnum.CanExcludeClientFromLuckyDraws)}
        onSelect={onToggleLuckyDrawExclusion}
      >
        Toggle lucky draw exclusion
      </DropdownMenuItem>
    </>
  )

  return (
    <Fragment>
      <ClientNavigation
        isEdit={isEdit}
        data={props.clientDetails}
        tab={ClientTab.OTHER}
        updateClientStatus={props.updateClientStatus}
      />
      <MainTemplate>
        <DataGrid
          columns={1}
          rightAddon={
            <ButtonGroup>
              {!isEdit ? (
                <Button
                  type='button'
                  onClick={() => setEdit(true)}
                  disabled={
                    !hasPermission(permissions, PermissionsEnum.CanEditClientOtherInformation)
                  }
                >
                  {'Edit'}
                </Button>
              ) : (
                <React.Fragment>
                  <Button
                    variant='outline'
                    className='is-danger'
                    type='button'
                    onClick={handleCancel}
                  >
                    {'Cancel'}
                  </Button>
                  <Button key='button' onClick={handleSubmitForm}>
                    {'Save'}
                  </Button>
                </React.Fragment>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      type='button'
                      variant='outline'
                      size='icon'
                      aria-label='More options'
                      className='more-button'
                    />
                  }
                >
                  <ChevronDown />
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>{renderMenu()}</DropdownMenuContent>
              </DropdownMenu>
            </ButtonGroup>
          }
        >
          <DataCard columns={2} title='Settings'>
            <FormCheckboxField
              readOnly={!isEdit}
              label='Personal Info Locked'
              name='isPersonalInfoLocked'
            />
            <FormCheckboxField
              readOnly={!isEdit}
              label='Address Info Locked'
              name='isAddressInfoLocked'
            />
            <FormCheckboxField
              readOnly={!isEdit}
              label='Additional Info Locked'
              name='isAdditionalInfoLocked'
            />
            <FormCheckboxField
              readOnly={!isEdit}
              label='Payment Info Locked'
              name='isPaymentInfoLocked'
              disabled={!hasPermission(permissions, PermissionsEnum.CanEditPaymentsDocument)}
            />
            <FormCheckboxField
              readOnly={!isEdit}
              label='KYC Update needed'
              name='isKYCUpdateNeeded'
              rightIcon={
                <Tooltip key='right'>
                  <TooltipTrigger>
                    <Info size={14} />
                  </TooltipTrigger>
                  <TooltipContent side='right'>
                    A KYC form is filled in upon registration. After a period of 1 year, i.e., 365
                    days, from the date of registration, they need to fill a KYC Update Form. Both
                    are made available via this field.
                    <br />
                    <br />
                    Check this field if a client is 1+ year old and needs to see a KYC Update Form
                    in their Client Area or has not not submitted the KYC Update Form already made
                    available to them. Leave it unchecked, if the client is less than 1 year old or
                    is more than 1 year old but has already submitted their KYC Update Form.
                  </TooltipContent>
                </Tooltip>
              }
            />
            <FormCheckboxField
              readOnly={!isEdit}
              label='IB Registration allowed'
              name='introducingBrokerRegistrationAllowed'
            />
            <FormCheckboxField
              readOnly={!isEdit}
              label='Receives newsletter'
              name='shouldReceiveNewsletter'
            />
            <FormCheckboxField
              readOnly={!isEdit}
              label='Internal test account'
              name='isInternalTestAccount'
            />
            <FormCheckboxField
              readOnly={!isEdit}
              label='Multitier Registration Allowed'
              name='multiTierRegistrationAllowed'
            />
            <FormSelectField
              options={kycProviders}
              label='KYC Verification Method'
              name='kycVerificationProviderId'
              readOnly={!isEdit}
              onChange={(value) => setFieldValue('kycVerificationProviderId', value)}
            />
          </DataCard>
          <DataCard title='Restrictions'>
            <FormMultiSelectField
              options={restrictions}
              label='Client Restrictions'
              name='restrictions'
              readOnly={!isEdit}
              showSearch
              onChange={(values) => {
                if (!values) return setFieldValue('restrictions', [])
                else {
                  const newRestrictions = restrictions.filter((r) =>
                    (values as number[]).includes(r.id)
                  )
                  setFieldValue('restrictions', newRestrictions)
                }
              }}
            />
          </DataCard>
        </DataGrid>
      </MainTemplate>
    </Fragment>
  )
}

export type ClientDetailsOtherFormValues = Omit<UpdateClientOther, 'restrictions'> & {
  restrictions: NameDto[]
}

interface OuterProps {
  clientDetails: ClientDetailedDto

  onSubmit(values: FormSubmitValues<UpdateClientOther>): Promise<void>

  updateClientStatus(values: UpdateClientStatus): void
  onMultiTierRegistrationAllow(): Promise<void>
  onToggleInterestRatesRestriction(): Promise<void>
  onToggleBonusCampaignExclusion(): Promise<void>
  onToggleLuckyDrawExclusion(): Promise<void>
}

export const ClientDetailsOtherForm = withFormik<OuterProps, ClientDetailsOtherFormValues>({
  mapPropsToValues: ({ clientDetails }) => {
    const currentClientDetails: ClientDetailedDto = clientDetails || {}
    return {
      isAddressInfoLocked: currentClientDetails.isAddressInfoLocked,
      isPersonalInfoLocked: currentClientDetails.isPersonalInfoLocked,
      isAdditionalInfoLocked: currentClientDetails.isAdditionalInfoLocked,
      isPaymentInfoLocked: currentClientDetails.isPaymentInfoLocked,
      isCompanyInfoLocked: currentClientDetails.isCompanyInfoLocked,
      isKYCUpdateNeeded: currentClientDetails.isKYCUpdateNeeded,
      kycVerificationProviderId:
        currentClientDetails.visibilityConfiguration.kycVerificationProvider,
      introducingBrokerRegistrationAllowed:
        currentClientDetails.introducingBrokerRegistrationAllowed,
      shouldReceiveNewsletter: currentClientDetails.shouldReceiveNewsletter,
      isInternalTestAccount: currentClientDetails.isInternalTestAccount,
      multiTierRegistrationAllowed: currentClientDetails.multiTierRegistrationAllowed,
      restrictions: currentClientDetails.restrictions,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  enableReinitialize: true,
})(ClientOtherFormUI)
